import { _ } from 'numeral';
import useGraphQLClient from './graphqlClient';
import {
  ProductionGenresCreateWithoutProductionInput,
  Productions,
  ProductionsCreateInput,
  ProductionsOrderByWithRelationInput,
  ProductionsUpdateInput,
  ProductionsWhereInput,
  ProductionTagsCreateWithoutProductionInput,
  ProductionSeriesCreateWithoutProductionInput,
  Series,
} from './spotwrite';
import tagService from './TagService';
import SeriesService from './SeriesService';
import { ProductionImagesIndexPost } from '../types/productionImages';
import ProductionImageService from '../services/ProductionImageService';

/**
 * 作品の検索条件
 */
export type PuroductionsSearchCondition = {
  keyword?: string;
  userId?: number;
  formId?: number;
  genreId?: number;
  seriesId?: number;
  tagName?: string;
  limit?: number;
  offset?: number;
  orderBy?: ProductionsOrderByWithRelationInput[];
};

class ProductionService {
  /**
   * 検索条件を整形します
   * @param condition
   * @returns
   */
  private buildSearchWhere = (condition: PuroductionsSearchCondition) => {
    const andWhere: ProductionsWhereInput[] = [];

    // 作者で絞り込み
    if (condition.userId) {
      andWhere.push({
        user: {
          is: { id: { equals: condition.userId } },
        },
      });
    }

    // 工程で絞り込み
    if (condition.formId) {
      andWhere.push({
        form: {
          is: { id: { equals: condition.formId } },
        },
      });
    }

    // シリーズで絞り込み
    if (condition.seriesId) {
      andWhere.push({
        productionSeries: {
          some: { series: { is: { id: { equals: condition.seriesId } } } },
        },
      });
    }

    // ジャンルで絞り込み
    if (condition.genreId) {
      andWhere.push({
        productionGenres: {
          some: { genre: { is: { id: { equals: condition.genreId } } } },
        },
      });
    }

    // タグで絞り込み
    if (condition.tagName) {
      andWhere.push({
        productionTags: {
          some: { tag: { is: { name: { equals: condition.tagName } } } },
        },
      });
    }

    if (condition.keyword) {
      andWhere.push({
        OR: [
          { title: { contains: condition.keyword } },
          { description: { contains: condition.keyword } },
          { body: { contains: condition.keyword } },
          {
            productionGenres: {
              some: { genre: { is: { name: { contains: condition.keyword } } } },
            },
          },
        ],
      });
    }

    return {
      AND: andWhere,
    };
  }

  /**
   * 最新の作品を取得
   * @param userId
   * @param limit
   */
  public latest = async (userId: number, limit: number) => {
    const condition: PuroductionsSearchCondition = {
      userId,
      limit: limit > 0 ? limit : 3,
    };
    return this.search(condition);
  }

  /**
   * 指定した検索条件に一致する作品の件数を取得
   * @param condition
   * @returns
   */
  public aggregateSearch = async (condition: PuroductionsSearchCondition) => {
    const client = useGraphQLClient();

    // 検索条件ビルド
    const where = this.buildSearchWhere(condition);

    try {
      const res = await client.aggregateProductions({
        where,
      });
      return res.aggregateProductions?.count;
    } catch (error) {
      console.log(error);
    }
    return null;
  };

  /**
   * 作品を検索
   * @param where
   */
  public search = async (condition: PuroductionsSearchCondition) => {
    const client = useGraphQLClient();

    // 検索条件ビルド
    const where = this.buildSearchWhere(condition);

    try {
      const res = await client.findManyProductions({
        where,
        take: condition.limit,
        skip: condition.offset,
        orderBy: condition.orderBy,
      });
      return res.findManyProductions as Productions[];
    } catch (error) {
      console.log(error);
    }
    return [];
  };

  /**
   * IDを指定して作品を取得します
   * @param id
   * @returns
   */
  public get = async (productionId: number) => {
    const client = useGraphQLClient();
    try {
      const res = await client.findUniqueProductions({
        where: { id: productionId },
      });
      return res.findUniqueProductions as Productions;
    } catch (error) {
      console.log(error);
    }
    return null;
  }

  /**
   * 作品を削除します
   * @param productionId
   */
  public delete = async (productionId: number) => {
    const client = useGraphQLClient();
    const work = await this.get(productionId);
    if (work) {
      try {
        const deleted = await client.deleteOneProductions({
          where: { id: work.id },
        });
        return deleted;
      } catch (error) {
        console.log(error);
      }
    }
    return null;
  }

  /**
   * 作品を登録します
   * @param production
   */
  public insert = async (data: any) => {
    const client = useGraphQLClient();
    try {
      // 挿入
      const input: ProductionsCreateInput = {
        title: data.title,
        body: data.body,
        description: data.description,
        layout: data.layout,
        onCircle: false,
        postFacebook: false,
        postInstagram: false,
        postTwitter: false,
        published: new Date(),
        commentAcceptable: data.commentAcceptable,
        form: {
          connect: {
            id: data.form.id,
          },
        },
        user: {
          connect: {
            id: data.userId,
          },
        },
      };
      // シリーズ
      if (!data.isNewSeries && data.series) {
        // 登録済みのシリーズの関連付け
        const series = data.series as Series;
        if (series) {
          input.productionSeries = {
            create: [
              { series: { connect: { id: series?.id } } },
            ],
          };
        }
      } else if (data.isNewSeries && data.newSeriesTitle) {
        // シリーズを登録して関連付け
        // シリーズを登録
        const series = await SeriesService.create(
          { title: data.newSeriesTitle, userId: data.userId },
        );
        // 登録済みのシリーズの関連付け
        if (series) {
          input.productionSeries = {
            create: [
              { series: { connect: { id: series?.id } } },
            ],
          };
        }
      }

      // ジャンルを関連付け
      if (data.genres && data.genres.length > 0) {
        const createGenres: ProductionGenresCreateWithoutProductionInput[] = [];
        data.genres.forEach((genreIdStr:any) => {
          const genreId = parseInt(genreIdStr, 10);
          createGenres.push({
            genre: {
              connect: {
                id: genreId,
              },
            },
          });
        });
        input.productionGenres = {
          create: createGenres,
        };
      }

      // タグを登録して関連付け
      if (data.tags && data.tags.length > 0) {
        const createTags: ProductionTagsCreateWithoutProductionInput[] = [];
        // タグを登録
        const tags = await Promise.all(
          data.tags.map((tagStr:string) => tagService.create({ name: tagStr })),
        );
        // 登録済みのタグの関連付け
        tags.forEach((tag) => {
          if (tag) {
            createTags.push({
              tag: {
                connect: {
                  id: tag.id,
                },
              },
            });
          }
        });
        input.productionTags = {
          create: createTags,
        };
      }

      // 作品を登録
      const res = await client.createOneProductions({
        data: input,
      });
      if (res.createOneProductions) {
        // if (data.pictures && data.pictures.length > 0) {
        //   const postData: ProductionImagesIndexPost = { productionImages: [] };
        //   console.log(data.pictures);
        //   data.pictures.forEach((picture: any) => {
        //     if (picture.image) {
        //       postData.productionImages?.push({ image: picture.image });
        //     }
        //   });
        //   ProductionImageService.updateWorkImages(res.createOneProductions.id, postData);
        // }
        return await this.get(res.createOneProductions.id);
      }
    } catch (error) {
      console.log(error);
    }
    return null;
  };

  /**
   * 作品を更新します
   * @param production
   */
  public update = async (data: any) => {
    const client = useGraphQLClient();
    try {
      const input: ProductionsUpdateInput = {
        title: { set: data.title },
        // body: { set: data.body },
        description: { set: data.description },
        layout: { set: data.layout },
        commentAcceptable: { set: data.commentAcceptable },
        form: {
          connect: {
            id: data.form.id,
          },
        },
        // onCircle: false,
        // postFacebook: false,
        // postInstagram: false,
        // postTwitter: false,
        // user: {
        //   connect: {
        //     id: 0,
        //   },
        // },
      };

      if (data.body) {
        input.body = { set: data.body };
      }

      // ジャンルを関連付け
      const createGenres: ProductionGenresCreateWithoutProductionInput[] = [];
      if (data.genres && data.genres.length > 0) {
        data.genres.forEach((genreIdStr:any) => {
          const genreId = parseInt(genreIdStr, 10);
          createGenres.push({
            genre: {
              connect: {
                id: genreId,
              },
            },
          });
        });
      }
      input.productionGenres = {
        deleteMany: [
          { productionId: { equals: data.workId } },
        ],
        create: createGenres,
      };
      // シリーズ
      if (!data.isNewSeries && data.series) {
        // 登録済みのシリーズの関連付け
        const series = data.series as Series;
        if (series) {
          input.productionSeries = {
            deleteMany: [
              { productionId: { equals: data.workId } },
            ],
            create: [
              { series: { connect: { id: series?.id } } },
            ],
          };
        }
      } else if (data.isNewSeries && data.newSeriesTitle) {
        // シリーズを登録して関連付け
        // シリーズを登録
        const series = await SeriesService.create(
          { title: data.newSeriesTitle, userId: data.userId },
        );
        // 登録済みのシリーズの関連付け
        if (series) {
          input.productionSeries = {
            deleteMany: [
              { productionId: { equals: data.workId } },
            ],
            create: [
              { series: { connect: { id: series?.id } } },
            ],
          };
        }
      } else {
        // シリーズの関連削除
        input.productionSeries = {
          deleteMany: [
            { productionId: { equals: data.workId } },
          ],
        };
      }

      // タグを登録して関連付け
      const createTags: ProductionTagsCreateWithoutProductionInput[] = [];
      if (data.tags && data.tags.length > 0) {
        // タグを登録
        const tags = await Promise.all(
          data.tags.map((tagStr:string) => tagService.create({ name: tagStr })),
        );
        // 登録済みのタグの関連付け
        tags.forEach((tag) => {
          if (tag) {
            createTags.push({
              tag: {
                connect: {
                  id: tag.id,
                },
              },
            });
          }
        });
      }
      input.productionTags = {
        deleteMany: [
          { productionId: { equals: data.workId } },
        ],
        create: createTags,
      };

      // 作品を更新
      const res = await client.updateOneProductions({
        data: input,
        where: { id: data.workId },
      });
      if (res.updateOneProductions) {
        // if (data.pictures && data.pictures.length > 0) {
        //   const postData: ProductionImagesIndexPost = { productionImages: [] };
        //   data.pictures.forEach((picture:any) => {
        //     if (picture) {
        //       postData.productionImages?.push({ image: picture.image });
        //     }
        //   });
        //   ProductionImageService.updateWorkImages(res.updateOneProductions.id, postData);
        // }
        return await this.get(res.updateOneProductions.id);
      }
    } catch (error) {
      console.log(error);
    }
    return null;
  };

  /**
   * 作品の画像を更新します
   * @param production
   */
  public updateImage = async (data: any) => {
    const client = useGraphQLClient();
    try {
      if (data.workId) {
        if (data.pictures && data.pictures.length > 0) {
          const postData: ProductionImagesIndexPost = { productionImages: [] };
          data.pictures.forEach((picture:any) => {
            if (picture) {
              postData.productionImages?.push({ image: picture.image });
            }
          });
          return await ProductionImageService.updateWorkImages(data.workId, postData);
        }
      }
    } catch (error) {
      console.log(error);
      return {
        error,
      };
    }
    return null;
  };

  /**
   * 閲覧数をカウントアップします
   * @param productionId
   */
  public incrementCountViews = async (productionId: number) => {
    const client = useGraphQLClient();
    if (productionId) {
      const res = await client.incrementViewCount({ productionId });
      return res;
    }
    return 0;
  }
}
export default new ProductionService();
